import { useEffect, useState } from 'react';

const TYPE_META = {
  feat:    { label: 'Fonctionnalité', color: '#22c55e', bg: '#14532d' },
  fix:     { label: 'Correction',     color: '#f59e0b', bg: '#451a03' },
  db:      { label: 'Base de données',color: '#34d399', bg: '#064e3b' },
  style:   { label: 'Interface',      color: '#38bdf8', bg: '#0c4a6e' },
  refactor:{ label: 'Refactoring',    color: '#a78bfa', bg: '#3b0764' },
  chore:   { label: 'Technique',      color: '#94a3b8', bg: '#1e293b' },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.chore;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 10px',
      borderRadius: '99px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: meta.bg,
      color: meta.color,
      flexShrink: 0,
    }}>
      {meta.label}
    </span>
  );
}

function groupByDate(entries) {
  const groups = {};
  for (const entry of entries) {
    if (!groups[entry.date]) groups[entry.date] = [];
    groups[entry.date].push(entry);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function Changelog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/changelog.json')
      .then((r) => {
        if (!r.ok) throw new Error('Impossible de charger le changelog');
        return r.json();
      })
      .then((data) => { setEntries(data); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  const groups = groupByDate(entries);

  const total = { feat: 0, fix: 0, db: 0, style: 0, refactor: 0, chore: 0 };
  entries.forEach((e) => { if (total[e.type] !== undefined) total[e.type]++; });

  return (
    <div>
      <h1 className="page-title">📝 Historique des versions</h1>
      <p className="page-subtitle">{entries.length} commit{entries.length !== 1 ? 's' : ''} enregistré{entries.length !== 1 ? 's' : ''}</p>

      {/* Résumé */}
      {!loading && !error && (
        <div className="grid grid-4" style={{ marginBottom: 28 }}>
          {Object.entries(TYPE_META).filter(([k]) => total[k] > 0).map(([type, meta]) => (
            <div key={type} className="card" style={{ padding: '16px 20px', borderLeft: `3px solid ${meta.color}` }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: meta.color }}>{total[type]}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{meta.label}</div>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text2)', padding: 40 }}>
          Chargement…
        </div>
      )}

      {error && (
        <div className="card" style={{ color: 'var(--danger)' }}>{error}</div>
      )}

      {!loading && !error && groups.map(([date, items]) => (
        <div key={date} style={{ marginBottom: 28 }}>
          {/* En-tête de date */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
          }}>
            <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--accent2)',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '4px 14px',
              whiteSpace: 'nowrap',
            }}>
              {formatDate(date)}
            </div>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
              {items.length} changement{items.length > 1 ? 's' : ''}
            </div>
          </div>

          {/* Liste des commits */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((entry) => (
              <div key={entry.hash} className="card" style={{
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}>
                <TypeBadge type={entry.type} />
                <span style={{ flex: 1, fontSize: 14, color: 'var(--text)' }}>
                  {entry.message}
                </span>
                <code style={{
                  fontSize: 11,
                  color: 'var(--text2)',
                  background: 'var(--bg3)',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontFamily: 'monospace',
                  flexShrink: 0,
                }}>
                  {entry.hash}
                </code>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
